/**
 * eslint-disable no-nested-ternary
 *
 * @format
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { api } from '../../../../api.ts';
import CustomAutocomplete from '../../../misc/CustomAutocomplete';

const initialFormData = {
  type: '',
  title: '',
  icon: '',
  is_active: true,
  main_typology: null,
  is_filter: false,
  default_products: [],
  vat_rate: null,
};

const initialQuestionData = {
  question: '',
  is_list: false,
  question_filter_string: '',
  question_order_by_string: '',
  can_pick_multiple: false,
  order: 0,
};

const initialAnswerData = {
  answer: '',
  related_products: [],
  related_brand: null,
  image: null,
  icon: '',
  icon_count: '',
  color: 'primary',
  order: 0,
  filter_string: '',
  can_pick_multiple: false,
  excluded_question_orders: '',
  parent_product_name: '',
  parent_product_vat_rate: null,
  parent_product_typology: null,
};

const COLOR_CHOICES = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'wow', label: 'Wow' },
  { value: 'purple', label: 'Purple' },
  { value: 'default', label: 'Default' },
];

function VirtualKitForm({
  open,
  onClose,
  onSave,
  config,
  isLoading: isSavingConfig,
}) {
  const [formData, setFormData] = useState(initialFormData);
  const [questions, setQuestions] = useState([]);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const [currentProductSelection, setCurrentProductSelection] = useState({
    questionIndex: null,
    answerIndex: null,
  });

  // Populate form if editing
  useEffect(() => {
    if (config && open) {
      setFormData({
        type: config.type || '',
        title: config.title || '',
        icon: config.icon || '',
        is_active: config.is_active !== false,
        main_typology: config.main_typology || null,
        is_filter: config.is_filter || false,
        default_products: config.default_products || [],
        vat_rate: config.vat_rate || null,
      });

      // Set questions with their answers
      if (config.questions) {
        setQuestions(
          config.questions.map((q) => ({
            id: q.id,
            question: q.question || '',
            is_list: q.is_list || false,
            question_filter_string: q.question_filter_string || '',
            question_order_by_string: q.question_order_by_string || '',
            can_pick_multiple: q.can_pick_multiple || false,
            order: q.order || 0,
            answers: (q.answers || []).map((a) => ({
              id: a.id,
              answer: a.answer || '',
              related_products: (a.related_products || []).map((p, idx) =>
                typeof p === 'object' && p !== null
                  ? {
                      ...p,
                      order: typeof p.order === 'number' ? p.order : idx,
                      quantity:
                        typeof p.quantity === 'number' && p.quantity > 0
                          ? p.quantity
                          : 1,
                    }
                  : { id: p, order: idx, quantity: 1 },
              ),
              related_brand: a.related_brand || null,
              image: a.image || null,
              icon: a.icon || '',
              icon_count: a.icon_count || '',
              color: a.color || 'primary',
              order: a.order || 0,
              filter_string: a.filter_string || '',
              can_pick_multiple: a.can_pick_multiple || false,
              excluded_question_orders: a.excluded_question_orders || '',
              parent_product_name: a.parent_product_name || '',
              parent_product_vat_rate: a.parent_product_vat_rate || null,
              parent_product_typology: a.parent_product_typology || null,
            })),
          })),
        );
      } else {
        setQuestions([]);
      }
    } else if (!config && open) {
      setFormData(initialFormData);
      setQuestions([]);
    }
  }, [config, open]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleQuestionChange = (questionIndex, field, value) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        [field]: value,
      };

      // Clear all answers when is_list is set to true
      if (field === 'is_list' && value === true) {
        newQuestions[questionIndex].answers = [];
      }

      return newQuestions;
    });
  };

  const handleAnswerChange = (questionIndex, answerIndex, field, value) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const newAnswers = [...newQuestions[questionIndex].answers];
      newAnswers[answerIndex] = {
        ...newAnswers[answerIndex],
        [field]: value,
      };
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answers: newAnswers,
      };
      return newQuestions;
    });
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        ...initialQuestionData,
        order: prev.length,
        answers: [],
      },
    ]);
  };

  const removeQuestion = (questionIndex) => {
    setQuestions((prev) => prev.filter((_, index) => index !== questionIndex));
  };

  const addAnswer = (questionIndex) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const currentAnswers = newQuestions[questionIndex].answers || [];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answers: [
          ...currentAnswers,
          {
            ...initialAnswerData,
            order: currentAnswers.length,
          },
        ],
      };
      return newQuestions;
    });
  };

  const removeAnswer = (questionIndex, answerIndex) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const newAnswers = newQuestions[questionIndex].answers.filter(
        (_, index) => index !== answerIndex,
      );
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        answers: newAnswers,
      };
      return newQuestions;
    });
  };

  const searchProducts = async (searchTerm) => {
    if (searchTerm.trim().length < 2) {
      setFilteredProducts([]);
      return;
    }

    try {
      const response = await api.get(
        `/products/v2/products/?search=${encodeURIComponent(searchTerm)}`,
      );
      setFilteredProducts(response.data.results || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    }
  };

  const addProductToAnswer = (product) => {
    const { questionIndex, answerIndex } = currentProductSelection;
    const currentAnswer = questions[questionIndex].answers[answerIndex];
    const currentProducts = currentAnswer.related_products || [];

    // Check if product is already added
    if (currentProducts.some((p) => p.id === product.id)) {
      return;
    }

    const newProducts = [
      ...currentProducts,
      { ...product, order: currentProducts.length, quantity: 1 },
    ];
    handleAnswerChange(
      questionIndex,
      answerIndex,
      'related_products',
      newProducts,
    );

    // Clear search and hide suggestions
    setProductSearchTerm('');
    setShowProductSuggestions(false);
    setFilteredProducts([]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    // Validation
    const errors = {};
    if (!formData.type.trim()) {
      errors.type = 'Le type est obligatoire';
    }

    if (!formData.main_typology) {
      errors.main_typology = 'Le type de kit est obligatoire';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError('Veuillez corriger les erreurs dans le formulaire.');
      return;
    }

    const payload = {
      ...formData,
      main_typology: formData.main_typology?.id || null,
      vat_rate: formData.vat_rate?.id || null,
      default_products: formData.default_products.map((p) => p.id),
      questions: questions.map((q) => ({
        id: q.id,
        question: q.question,
        is_list: q.is_list,
        question_filter_string: q.question_filter_string,
        question_order_by_string: q.question_order_by_string,
        can_pick_multiple: q.can_pick_multiple,
        order: q.order,
        answers: q.answers.map((a) => ({
          id: a.id,
          answer: a.answer,
          related_products: [...a.related_products]
            .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
            .map((p) => ({
              id: p.id,
              order: p.order ?? 0,
              quantity: p.quantity > 0 ? p.quantity : 1,
            })),
          related_brand: a.related_brand?.id || null,
          icon: a.icon,
          icon_count: a.icon_count,
          color: a.color,
          order: a.order,
          filter_string: a.filter_string,
          can_pick_multiple: a.can_pick_multiple,
          excluded_question_orders: a.excluded_question_orders,
          parent_product_name: a.parent_product_name,
          parent_product_vat_rate: a.parent_product_vat_rate?.id || null,
          parent_product_typology: a.parent_product_typology?.id || null,
        })),
      })),
    };

    try {
      await onSave(payload, config?.id);
    } catch (error) {
      console.error('Save config error:', error);
      const errData = error.response?.data;
      if (errData && typeof errData === 'object') {
        setFieldErrors(errData);
        const messages = Object.entries(errData)
          .map(
            ([field, msgs]) =>
              `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`,
          )
          .join('; ');
        setFormError(
          messages ||
            error.message ||
            'Une erreur inconnue est survenue lors de la sauvegarde.',
        );
      } else {
        setFormError(
          error.message ||
            'Une erreur inconnue est survenue lors de la sauvegarde.',
        );
      }
    }
  };

  const renderQuestionForm = (question, questionIndex) => (
    <Accordion key={`question-${question.id || questionIndex}`} defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">
          Question {questionIndex + 1}:{' '}
          {question.question || 'Nouvelle question'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Question"
              value={question.question}
              onChange={(e) =>
                handleQuestionChange(questionIndex, 'question', e.target.value)
              }
              fullWidth
              required
              disabled={isSavingConfig}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Ordre"
              type="number"
              value={question.order}
              onChange={(e) =>
                handleQuestionChange(
                  questionIndex,
                  'order',
                  parseInt(e.target.value, 10) || 0,
                )
              }
              fullWidth
              disabled={isSavingConfig}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <IconButton
              onClick={() => removeQuestion(questionIndex)}
              disabled={isSavingConfig}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Filtre de question"
              value={question.question_filter_string}
              onChange={(e) =>
                handleQuestionChange(
                  questionIndex,
                  'question_filter_string',
                  e.target.value,
                )
              }
              fullWidth
              disabled={isSavingConfig}
              helperText="Chaîne de filtrage pour cette question"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Ordre de tri"
              value={question.question_order_by_string}
              onChange={(e) =>
                handleQuestionChange(
                  questionIndex,
                  'question_order_by_string',
                  e.target.value,
                )
              }
              fullWidth
              disabled={isSavingConfig}
              helperText="Ex: supplier_price ou -supplier_price pour ordre décroissant"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={question.is_list}
                  onChange={(e) =>
                    handleQuestionChange(
                      questionIndex,
                      'is_list',
                      e.target.checked,
                    )
                  }
                  disabled={isSavingConfig}
                />
              }
              label="Est une liste"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={question.can_pick_multiple}
                  onChange={(e) =>
                    handleQuestionChange(
                      questionIndex,
                      'can_pick_multiple',
                      e.target.checked,
                    )
                  }
                  disabled={isSavingConfig}
                />
              }
              label="Sélection multiple"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {!question.is_list && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Réponses
              </Typography>
            </Box>

            {question.answers.map((answer, answerIndex) => (
              <Card key={`answer-${answer.id || answerIndex}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Réponse"
                        value={answer.answer}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'answer',
                            e.target.value,
                          )
                        }
                        fullWidth
                        required
                        disabled={isSavingConfig}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Ordre"
                        type="number"
                        value={answer.order}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'order',
                            parseInt(e.target.value, 10) || 0,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <IconButton
                        onClick={() => removeAnswer(questionIndex, answerIndex)}
                        disabled={isSavingConfig}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ position: 'relative' }}>
                        <TextField
                          label="Produits associés"
                          value={productSearchTerm}
                          onChange={(e) => {
                            const { value } = e.target;
                            setProductSearchTerm(value);
                            if (value.trim().length >= 2) {
                              searchProducts(value);
                              setShowProductSuggestions(true);
                              setCurrentProductSelection({
                                questionIndex,
                                answerIndex,
                              });
                            } else {
                              setShowProductSuggestions(false);
                              setFilteredProducts([]);
                            }
                          }}
                          onFocus={() => {
                            if (productSearchTerm.trim().length >= 2) {
                              setShowProductSuggestions(true);
                              setCurrentProductSelection({
                                questionIndex,
                                answerIndex,
                              });
                            }
                          }}
                          fullWidth
                          disabled={isSavingConfig}
                          helperText="Tapez pour rechercher des produits"
                          placeholder="Rechercher des produits..."
                        />

                        {/* Product Suggestions Dropdown */}
                        {showProductSuggestions &&
                          currentProductSelection.questionIndex ===
                            questionIndex &&
                          currentProductSelection.answerIndex === answerIndex &&
                          filteredProducts.length > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1000,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                maxHeight: 200,
                                overflow: 'auto',
                                boxShadow: 3,
                              }}
                            >
                              {filteredProducts.map((product) => (
                                <Box
                                  key={product.id}
                                  sx={{
                                    p: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                  }}
                                  onClick={() => addProductToAnswer(product)}
                                >
                                  <Typography variant="body2" fontWeight="bold">
                                    {product.internal_name ||
                                      product.public_name ||
                                      'Produit'}
                                  </Typography>
                                  {product.public_name &&
                                    product.internal_name && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {product.public_name}
                                      </Typography>
                                    )}
                                </Box>
                              ))}
                            </Box>
                          )}

                        {/* Selected Products */}
                        {answer.related_products.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {[...answer.related_products]
                              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                              .map((product, productIndex) => (
                                <Box
                                  key={product.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <TextField
                                    label="Ordre"
                                    type="number"
                                    size="small"
                                    value={product.order ?? productIndex}
                                    onChange={(e) => {
                                      const newOrder = parseInt(
                                        e.target.value,
                                        10,
                                      );
                                      if (
                                        !Number.isFinite(newOrder) ||
                                        newOrder < 0
                                      )
                                        return;
                                      const newProducts =
                                        answer.related_products.map((p) =>
                                          p.id === product.id
                                            ? { ...p, order: newOrder }
                                            : p,
                                        );
                                      handleAnswerChange(
                                        questionIndex,
                                        answerIndex,
                                        'related_products',
                                        newProducts,
                                      );
                                    }}
                                    sx={{ width: 64 }}
                                    disabled={isSavingConfig}
                                    inputProps={{
                                      min: 0,
                                      'aria-label': 'Ordre',
                                    }}
                                  />
                                  <TextField
                                    label="Qté"
                                    type="number"
                                    size="small"
                                    value={product.quantity ?? 1}
                                    onChange={(e) => {
                                      const newQuantity = parseInt(
                                        e.target.value,
                                        10,
                                      );
                                      if (
                                        !Number.isFinite(newQuantity) ||
                                        newQuantity < 1
                                      )
                                        return;
                                      const newProducts =
                                        answer.related_products.map((p) =>
                                          p.id === product.id
                                            ? { ...p, quantity: newQuantity }
                                            : p,
                                        );
                                      handleAnswerChange(
                                        questionIndex,
                                        answerIndex,
                                        'related_products',
                                        newProducts,
                                      );
                                    }}
                                    sx={{ width: 64 }}
                                    disabled={isSavingConfig}
                                    inputProps={{
                                      min: 1,
                                      'aria-label': 'Quantité',
                                    }}
                                  />
                                  <Chip
                                    label={
                                      product.internal_name ||
                                      product.public_name ||
                                      'Produit'
                                    }
                                    onDelete={() => {
                                      const newProducts =
                                        answer.related_products.filter(
                                          (p) => p.id !== product.id,
                                        );
                                      handleAnswerChange(
                                        questionIndex,
                                        answerIndex,
                                        'related_products',
                                        newProducts,
                                      );
                                    }}
                                    sx={{ flex: 1 }}
                                  />
                                </Box>
                              ))}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomAutocomplete
                        endpoint={(q) =>
                          `/products/v2/brands/${
                            q ? `?search=${encodeURIComponent(q)}` : ''
                          }`
                        }
                        getOptionLabel={(option) => option.name || ''}
                        getOptionKey={(option) => option.id}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        label="Marque associée"
                        value={answer.related_brand}
                        onChange={(e, newValue) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'related_brand',
                            newValue,
                          )
                        }
                        disabled={isSavingConfig}
                        fullWidth
                        allowNull
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Icône (FontAwesome v6)"
                        value={answer.icon}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'icon',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                        helperText="Ex: fa-solar-panel"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Compteur d'icône"
                        value={answer.icon_count}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'icon_count',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                        helperText="Ex: 12"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Couleur"
                        value={answer.color}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'color',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                      >
                        {COLOR_CHOICES.map((color) => (
                          <MenuItem key={color.value} value={color.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip
                                label={color.label}
                                color={
                                  color.value === 'default'
                                    ? 'default'
                                    : color.value
                                }
                                size="small"
                                sx={{ mr: 1 }}
                              />
                              {color.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Chaîne de filtrage"
                        value={answer.filter_string}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'filter_string',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                        helperText="Chaîne de filtrage pour cette réponse"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Questions exclues (ordres)"
                        value={answer.excluded_question_orders}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'excluded_question_orders',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                        helperText="Ex: 2,3,4 pour exclure les questions d'ordre 2, 3 et 4"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>
                        <Chip
                          label="Produit parent (si non trouvé)"
                          size="small"
                        />
                      </Divider>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Nom du produit parent"
                        value={answer.parent_product_name}
                        onChange={(e) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'parent_product_name',
                            e.target.value,
                          )
                        }
                        fullWidth
                        disabled={isSavingConfig}
                        helperText="Nom à utiliser si aucun produit parent trouvé"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CustomAutocomplete
                        endpoint={(q) =>
                          `/products/v2/typologies/${
                            q ? `?search=${encodeURIComponent(q)}` : ''
                          }`
                        }
                        getOptionLabel={(option) => option.name || ''}
                        getOptionKey={(option) => option.slug}
                        isOptionEqualToValue={(o, v) => o.slug === v.slug}
                        label="Typologie du produit parent"
                        value={answer.parent_product_typology}
                        onChange={(e, newValue) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'parent_product_typology',
                            newValue,
                          )
                        }
                        disabled={isSavingConfig}
                        fullWidth
                        allowNull
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CustomAutocomplete
                        endpoint={(q) =>
                          `/leads-vat-rates/${
                            q ? `?search=${encodeURIComponent(q)}` : ''
                          }`
                        }
                        getOptionLabel={(option) =>
                          `${option?.label || option}`
                        }
                        getOptionKey={(option) => option.value}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        label="TVA du produit parent"
                        value={answer.parent_product_vat_rate}
                        onChange={(e, newValue) =>
                          handleAnswerChange(
                            questionIndex,
                            answerIndex,
                            'parent_product_vat_rate',
                            newValue,
                          )
                        }
                        disabled={isSavingConfig}
                        fullWidth
                        allowNull
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={answer.can_pick_multiple}
                            onChange={(e) =>
                              handleAnswerChange(
                                questionIndex,
                                answerIndex,
                                'can_pick_multiple',
                                e.target.checked,
                              )
                            }
                            disabled={isSavingConfig}
                          />
                        }
                        label="Sélection multiple autorisée"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => addAnswer(questionIndex)}
              disabled={isSavingConfig}
              sx={{ mt: 2 }}
            >
              Ajouter une réponse
            </Button>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography variant="h5" component="span">
          {config
            ? 'Modifier la configuration'
            : 'Créer une nouvelle configuration'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <form id="virtual-kit-config-form" onSubmit={handleSubmit}>
          {formError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setFormError(null)}
            >
              {formError}
            </Alert>
          )}

          {/* Basic Configuration */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Configuration de base
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="type"
                label="Type"
                value={formData.type}
                onChange={handleInputChange}
                fullWidth
                required
                disabled={isSavingConfig}
                error={!!fieldErrors.type}
                helperText={
                  fieldErrors.type || 'Identifiant technique du wizard'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="title"
                label="Titre (assistant)"
                value={formData.title}
                onChange={handleInputChange}
                fullWidth
                disabled={isSavingConfig}
                error={!!fieldErrors.title}
                helperText={
                  fieldErrors.title ||
                  "Nom affiché dans l'assistant kit virtuel"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="icon"
                label="Icône (FontAwesome v6)"
                value={formData.icon}
                onChange={handleInputChange}
                fullWidth
                disabled={isSavingConfig}
                error={!!fieldErrors.icon}
                helperText={
                  fieldErrors.icon ||
                  'Ex: fa-duotone fa-thin fa-charging-station'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    disabled={isSavingConfig}
                  />
                }
                label="Actif (visible dans l'assistant)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                endpoint={(q) =>
                  `/products/v2/typologies/${
                    q ? `?search=${encodeURIComponent(q)}` : ''
                  }`
                }
                getOptionLabel={(option) => option.name || ''}
                getOptionKey={(option) => option.slug}
                isOptionEqualToValue={(o, v) => o.slug === v.slug}
                label="Typologie principale"
                value={formData.main_typology}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, main_typology: newValue }))
                }
                disabled={isSavingConfig}
                fullWidth
                allowNull
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomAutocomplete
                endpoint={(q) =>
                  `/leads-vat-rates/${
                    q ? `?search=${encodeURIComponent(q)}` : ''
                  }`
                }
                getOptionLabel={(option) => `${option?.label || option}`}
                getOptionKey={(option) => option.value}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                label="Taux de TVA"
                value={formData.vat_rate}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, vat_rate: newValue }))
                }
                disabled={isSavingConfig}
                fullWidth
                allowNull
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_filter"
                    checked={formData.is_filter}
                    onChange={handleInputChange}
                    disabled={isSavingConfig}
                  />
                }
                label="Est un filtre"
              />
            </Grid>
            {/* NOT USED YET */}
            {/* <Grid item xs={12}>
              <CustomAutocomplete
                multiple
                endpoint={(q) => `/products/v2/admin-products/${q ? `?search=${encodeURIComponent(q)}` : ''}`}
                getOptionLabel={(option) => option.public_name || ''}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                label="Produits par défaut"
                value={formData.default_products}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, default_products: newValue }))}
                disabled={isSavingConfig}
                fullWidth
              />
            </Grid> */}
          </Grid>

          {/* Questions Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Questions et réponses
          </Typography>

          {questions.map((question, questionIndex) =>
            renderQuestionForm(question, questionIndex),
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQuestion}
            disabled={isSavingConfig}
            sx={{ mb: 2 }}
          >
            Ajouter une question
          </Button>

          {questions.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Aucune question configurée. Ajoutez des questions pour créer un
              kit virtuel interactif.
            </Alert>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSavingConfig}>
          Annuler
        </Button>
        <Button
          type="submit"
          form="virtual-kit-config-form"
          variant="contained"
          color="primary"
          disabled={isSavingConfig}
        >
          {isSavingConfig && <CircularProgress size={24} />}
          {!isSavingConfig && config && 'Mettre à jour'}
          {!isSavingConfig && (!config || !config.id) && 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VirtualKitForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  config: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    is_active: PropTypes.bool,
    main_typology: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    is_filter: PropTypes.bool,
    default_products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        public_name: PropTypes.string,
      }),
    ),
    vat_rate: PropTypes.shape({
      id: PropTypes.number,
      rate: PropTypes.number,
    }),
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        question: PropTypes.string,
        is_list: PropTypes.bool,
        question_filter_string: PropTypes.string,
        can_pick_multiple: PropTypes.bool,
        order: PropTypes.number,
        answers: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            answer: PropTypes.string,
            related_products: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number,
                public_name: PropTypes.string,
                order: PropTypes.number,
                quantity: PropTypes.number,
              }),
            ),
            related_brand: PropTypes.shape({
              id: PropTypes.number,
              name: PropTypes.string,
            }),
            icon: PropTypes.string,
            icon_count: PropTypes.string,
            color: PropTypes.string,
            order: PropTypes.number,
            filter_string: PropTypes.string,
            can_pick_multiple: PropTypes.bool,
          }),
        ),
      }),
    ),
  }),
  isLoading: PropTypes.bool,
};

VirtualKitForm.defaultProps = {
  config: null,
  isLoading: false,
};

export default VirtualKitForm;
